import type {
  ExtendedInitiative,
  FocusData,
  Initiative,
  KPIOverviewData,
} from '../../api/initiative-client/initiative-client.type';
import type { Board, ProjectsResponse } from '../../api/projects-client/projects-client.type';
import type { TransformedTarget } from '../../api/targets-client/targets-client.type';
import type { Epic, MinifiedTask, TaskStatus } from '../../api/tasks-client/task-client.type';
import type {
  FlowByPhaseDataOverTime,
  MeasureDataResponse,
  MeasureMetadata,
} from '../../api/work-periods-client/work-periods-client.type';
import { CostMode } from '../../containers/strategy-v2/initiative-performance/cost-tab/cost-tab.type';
import type { Phase } from '../../containers/strategy-v2/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.type';
import {
  EpicSortOption,
  FocusMode,
  PerformanceTab,
  SortDirection,
  ThroughputMode,
} from '../../containers/strategy-v2/initiative-performance/initiative-performance.type';
import type { RelatedTasksMode } from '../../containers/strategy-v2/initiative-performance/related-tasks-tab/related-tasks-tab.type';
import { MeasureDataType } from '../../containers/strategy-v2/initiative-performance/targets-tab/targets-tab.type';
import { Metric } from '../../containers/strategy-v2/strategy-v2.type';

enum MessageStatus {
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
}

type Message = {
  status: MessageStatus;
  text: string;
};

type Filters = {
  status: string[];
  state: string[];
  source: string[];
  is_active: string[];
};

enum View {
  Table = 'table',
  TreeMap = 'tree-map',
}

enum TreeMapMetric {
  CostByInitiative = 'cost_by_initiative',
  EffortByInitiative = 'effort_by_initiative',
}

enum Panel {
  Filters = 'filters',
  Side = 'side',
  OtherInitiatives = 'other-initiatives',
}

type State = {
  initiatives: Initiative[];
  kpiOverviewData: KPIOverviewData | null;
  view: View;
  page: number;
  selectedInitiative: Initiative | null;
  initiativePerformance: ExtendedInitiative | null;
  performanceTab: PerformanceTab;
  filters: Filters;
  collapsedGroups: Record<string, boolean>;
  activePanel: Panel | null;
  searchTerm: string;
  treeMapMetric: TreeMapMetric;
  epics: Epic[];
  message: Message | null;
};

type ThroughputTabState = {
  mode: ThroughputMode;
  metric: Metric;
  selectedEpics: Epic[];
  selectedTeams: ProjectsResponse[];
  activePanel: Panel | null;
  selectedEpic: Epic | null;
  selectedTeam: ProjectsResponse | null;
  selectedDate: Date | null;
};

type FocusTabState = {
  metric: Metric;
  mode: FocusMode;
  selectedTeams: ProjectsResponse[];
  selectedBoards: Board[];
  focusTeamsData: FocusData | null;
  focusBoardsData: FocusData | null;
};

type FilterControls = {
  searchTerm: string;
  sortBy: EpicSortOption;
  sortDirection: SortDirection;
  hideEmpty: boolean;
};

type FlowByPhaseTabState = {
  metric: Metric;
  selectedDate: string | null;
  selectedEpic: string | null;
  visiblePhases: Record<string, boolean>;
  data: FlowByPhaseDataOverTime | null;
  filterControls: FilterControls;
};

type TargetsTabState = {
  targets: TransformedTarget[];
  selectedTarget: TransformedTarget | null;
  availableMeasures: MeasureMetadata[];
  measureData: MeasureDataResponse | null;
  visibleMeasures: Record<MeasureDataType, boolean>;
};

type CostTabState = {
  mode: CostMode;
  selectedEpics: Epic[];
  selectedTeams: ProjectsResponse[];
  filterControls: FilterControls;
  selectedEpic: Epic | null;
  selectedTeam: ProjectsResponse | null;
  activePanel: Panel | null;
};

type RelatedTasksTabState = {
  mode: RelatedTasksMode;
  selectedEpic: string | null;
  selectedTeam: string | null;
  selectedDate: Date | null;
  selectedStatuses: TaskStatus[];
  selectedPhase: Phase | null;
  searchTerm: string;
  page: number;
  selectedTask: MinifiedTask | null;
  filterControls: FilterControls;
  selectedThroughput: 'total' | 'completed';
  tasks: MinifiedTask[];
  activePanel: Panel | null;
};

export { MessageStatus, Panel, TreeMapMetric, View };
export type {
  CostTabState,
  FilterControls,
  Filters,
  FlowByPhaseTabState,
  FocusTabState,
  Message,
  RelatedTasksTabState,
  State,
  TargetsTabState,
  ThroughputTabState,
};
